import identity from 'lodash/identity';
import type { ComponentType } from 'react';
import { createElement as e, useCallback, useContext, useState } from 'react';
import ReactDOM from 'react-dom';
import CustomElementContext from '../contexts/CustomElementContext';
import remapProps from '../functions/remapProps';
import enhance from './enhance';

/**
 * Defines a React component type to be renderable as an HTML custom element.
 *
 * The React component will be rendered directly into the custom element,
 * using the element as the component's render host. It will
 * therefore contain whatever elements the component renders and this will be
 * visible from the DOM. No shadow DOM will be used; the component's elements
 * will appear directly inside the DOM where the custom element appears. (A
 * future version of this function may offer the use of a shadow DOM as an
 * optional feature.)
 *
 * The React component will respond to DOM modification events relating to the
 * the host custom element:
 *
 * - Initial appearance of the custom element in the DOM will result in initially rendering the React component,
 * - Removal of the custom element from the DOM will result in unmounting the React element, and
 * - Changes to the custom element's attributes listed in the `attributes` parameter will result in updating the component's properties.
 *
 * This function will ignore any attempt to register a custom tag that has already been registered.
 *
 * @example
 * ```tsx
 *
 * // --- React ---
 *
 * import { memo } from 'react';
 * import { defineCustomElement } from '@rcm/react-utils';
 *
 * defineCustomElement({
 *      attrs: ['data-animal', 'data-sound'],
 *      component: (props: { animal: string, sound: string }) => (
 *          <div>The {props.animal} says "{props.sound}"!</div>
 *      ),
 *      enhancers: [memo],
 *      mapAttrsToProps: (attrs) => ({
 *          animal: attrs['data-animal'],
 *          sound: attrs['data-sound']
 *      })
 *      tag: 'my-custom-element',
 * );
 *
 *
 * // --- HTML ---
 *
 *
 * ```
 *
 * ```html
 * <!doctype html>
 * <html>
 *      <head>
 *          <script src="./bundle.js"></script>
 *      </head>
 *      <body>
 *          <my-custom-element data-animal="dog" data-sound="woof" />
 *          <!-- Rendered as:
 *              <my-custom-element data-animal="dog" data-sound="woof">
 *                  <div>The dog says "woof"!</div>
 *              </my-custom-element>
 *          -->
 *      </body>
 * </html>
 * ```
 */
const defineCustomElement = <
  A extends string,
  E extends ((c: ComponentType<P>) => ComponentType<P>)[],
  P extends {} = { [K in A]: string }
>(
  options: {
    /**
     * The component that will be wrapped.
     */
    component: ComponentType<P>;

    /**
     * If provided, a new component will be created by applying
     * each of the given enhancers from left to right.
     */
    enhancers?: E;

    /**
     * Tag name that will instantiate the component in the browser
     */
    tag: string;
  } & (
    | {
        /**
         * Optional when 'mapAttrsToProps' is not used. If specified, these
         * custom element attributes will be passed to the component being
         * wrapped as properties. The property names will exactly match those
         * given in the custom element, and the values will always be strings.
         */
        attrs?: A[];
      }
    | {
        /**
         * Optional when 'mapAttrsToProps' is not used. If specified, these
         * custom element attributes will be passed to the component being
         * wrapped as properties. The property names will exactly match those
         * given in the custom element, and the values will always be strings.
         */
        attrs: A[];
        /**
         * Optional. If omitted, attributes are passed directly to the component
         * as-is, but when present this function will be passed the attributes
         * and the value returned will be passed to the wrapped component instead.
         */
        mapAttrsToProps?: (attrs: { [K in A]: string }) => P;
      }
  )
) => {
  if (window.customElements.get(options.tag)) {
    return;
  }

  /**
   * Applies the enhancers provided, as well as an additional enhancer to apply map attrs to props
   */
  const enhancedComponent = enhance(
    ...(options.enhancers ?? []),
    'mapAttrsToProps' in options ? remapProps(options.mapAttrsToProps) : identity
  )(options.component);

  /**
   * React component type that connects the wrapped component to the custom element
   */
  const CustomElementComponent = ({
    initialAttrs,
  }: {
    /**
     * Attributes of the custom element at time of its creation
     */
    initialAttrs: P;
  }) => {
    const [_attrs, _setAttrs] = useState(initialAttrs);
    const ctx = useContext(CustomElementContext);
    ctx.setAttr = useCallback((k, v) => _setAttrs({ ..._attrs, [k]: v }), [_attrs]);
    return e(enhancedComponent, _attrs);
  };

  class CustomElement extends HTMLElement {
    /**
     * Holds the context value for wrapped react component instance
     */
    #context: {
      element?: CustomElement;
      setAttr?: (k: string, v: string) => void;
    } = {};

    /**
     * Indicates what attributes will trigger the attribute change callback.
     *
     * Browsers require this. If this is not defined, the browser will not
     * trigger callbacks when attributes change.
     */
    static observedAttributes = options.attrs;

    /**
     * Called by the browser when the element is added to the DOM
     */
    public connectedCallback() {
      this.#context.element = this;
      ReactDOM.render(
        e(
          CustomElementContext.Provider,
          { value: this.#context },
          e(CustomElementComponent, {
            initialAttrs: getElementAttributes(this) as P,
          })
        ),
        this
      );
    }

    /**
     * Called by the browser when the element is removed from the DOM
     */
    public disconnectedCallback() {
      ReactDOM.unmountComponentAtNode(this);
    }

    /**
     * Called by the browser when any attribute on the element changes
     *
     * @param name Name of the element changed
     * @param oldValue Value as it was before the change
     * @param newValue Value after the change
     */
    public attributeChangedCallback(name: string, oldValue: string, newValue: string) {
      this.#context?.setAttr(name, newValue);
    }
  }

  window.customElements.define(options.tag, CustomElement);
};

/**
 * Returns attributes of an HTML element as a key-value record.
 */
const getElementAttributes = (el: HTMLElement): Record<string, string> => {
  const attrs = {} as Record<string, string>;
  // For-Of doesn't work on type "NamedNodeMap"
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < el.attributes.length; i++) {
    attrs[el.attributes[i].name] = el.attributes[i].value;
  }
  return attrs;
};

export default defineCustomElement;
